import axios from "axios";
import { parseFile } from "./helpers/file-parser";
import { mapTag } from "./helpers/tag-mapper";
import { mapStateToImportConfig } from "./helpers/import-mapper";
import { ImportUploadStatus } from "../const/import-status";
import {ImportFieldType} from "../const/field-type";

function runDemoUpload(commit) {
  setTimeout(() => {
    commit("setUploadProgress", 70);
  }, 1000);
  setTimeout(() => {
    commit("setUploadProgress", 100);
    commit("setImportId", new Date().getTime());
    commit("setImportStatus", ImportUploadStatus.Success);
  }, 3000);
}

export const importActions = {
  setDefaultState({ commit }) {
    commit("setDefaultState");
  },
  updateActionType({ commit }, actionType) {
    commit("setActionType", actionType);
  },
  setFileToUpload({ state, commit }, file) {
    commit("setMappingPreviewLoading", true);
    commit('setAiProcessed', false);
    commit('setValidatedFileChecksum', null);
    commit('setMappingSuggestionError', false);
    commit("setPreviewRows", []);
    parseFile(
      {
        file,
        delimiter: state.delimiter,
      },
      (result) => {
        commit("setMappingPreviewLoading", false);
        commit("setImportType", result.type);
        commit("setPreviewRows", result.previewRows);
        commit("setNoOfRows", result.noOfRows);
        commit("setSkipFirstLine", result.skipFirstLine);
      }
    );

    commit("setFileToUpload", file);
  },
  fetchTags({ state, commit }) {
    if (state.tags.importTags.length > 0) {
      return;
    }

    commit("setTagsLoading", true);
    return axios
      .get("/json/settings/import/tags")
      .then(({ data: { tagList } }) => {
        commit("setTags", tagList.filter((t) => !t.isDeleted).map(mapTag));
      })
      .finally(() => {
        commit("setTagsLoading", false);
      });
  },
  setFieldMapping({ commit }, { colIndex, fieldMapping }) {
    commit("setFieldMapping", { colIndex, fieldMapping });
  },
  toggleTag({ commit }, id) {
    commit("updateTagSelection", id);
  },
  addTag({ state, commit }) {
    axios
      .put("/tags", {
        tag_name: state.tags.newTagName,
      })
      .then(({ data }) => {
        if (!data) {
          window.showToast("Cannot create new tag - try again in a while", "error");
        } else {
          commit("addTag", Object.assign(mapTag(data), { isSelected: true }));
          commit("updateNewTagName", "");
          window.showToast("New tag has been created", "success");
        }
      });
  },
  loadImportHistory({ commit }) {
    axios
      .post("/json/settings/getImportHistory")
      .then(
        ({
          data: {
            data: { dataImportHistory },
          },
        }) => {
          commit("setImportHistory", dataImportHistory);
        }
      )
      .catch(() => {
        window.showToast("Cannot fetch import history - try again later", "warn");
      });
  },
  loadImportStatistics({ commit }, dataImportId) {
    axios
      .get("/json/settings/import/statistics", {
        params: {
          dataImportId: dataImportId,
        },
      })
      .then((request) => {
        commit("setImportStatistics", {
          dataImportId: dataImportId,
          statistics: request.data,
        });
      });
  },
  loadImportErrors({ commit }, dataImportId) {
    axios.post("/json/settings/get_single_history", { dataImportId: dataImportId }).then((request) => {
      commit("setImportErrors", {
        dataImportId: dataImportId,
        errors: request.data.data.dataImportErrors,
      });
    });
  },
  uploadFile({ commit, state, rootGetters }) {
    if (!state.fileToUpload) {
      return;
    }

    const formData = new FormData();
    formData.append("file", state.fileToUpload);
    formData.append("checksum", state.checksum);
    formData.append("aiProcessed", state.fieldMappings.aiProcessed ? "1" : "0");

    commit("setUploadProgress", 0);
    commit("setImportStatus", ImportUploadStatus.Uploading);

    if (rootGetters["global/isDemo"]) {
      runDemoUpload(commit);
      return;
    }

    axios
      .post("/settings/import_file", formData, {
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          commit("setUploadProgress", progress);
        },
      })
      .then(
        ({
          data: {
            data: { status, mappingResponse },
          },
        }) => {
          if (status === "success") {
            commit("setImportId", mappingResponse.dataImportId);
            return axios.post("/json/settings/submit_mapping", mapStateToImportConfig(state));
          } else {
            throw new Error(mappingResponse);
          }
        }
      )
      .then(() => {
        commit("setImportStatus", ImportUploadStatus.Success);
      })
      .catch((err) => {
        console.error(err);
        commit("setImportStatus", ImportUploadStatus.Error);
      });
  },
  updateNewTagName({ commit }, tagName) {
    commit("updateNewTagName", tagName);
  },
  setSubscriptionSource({ commit }, source) {
    commit("setSubscriptionSource", source);
  },
  setResurrectDeletedCustomer({ commit }, source) {
    commit("setResurrectDeletedCustomer", source);
  },
  setTreatNegativeStatusAsUnknown({ commit }, treatNegativeStatusAsUnknown) {
    commit("setTreatNegativeStatusAsUnknown", treatNegativeStatusAsUnknown);
  },
  setSubscriptionStatusEmail({ commit }, status) {
    commit("setSubscriptionStatusEmail", status);
  },
  setSubscriptionStatusSms({ commit }, status) {
    commit("setSubscriptionStatusSms", status);
  },
  setSubscriptionStatusWhatsapp({ commit }, status) {
    commit("setSubscriptionStatusWhatsapp", status);
  },
  setDuplicatesAction({ commit }, action) {
    commit("setDuplicatesAction", action);
  },
  setDelimiter({ commit }, delimiter) {
    commit("setDelimiter", delimiter);
  },
  setSkipFirstLine({ commit }, skipFirstLine) {
    commit("setSkipFirstLine", skipFirstLine);
  },
  setTagsAction({ commit }, action) {
    commit("setTagsAction", action);
  },
  async validateAndSuggestMapping({ commit, state }) {
    if (state.fieldMappings.validatedFileChecksum === state.checksum) {
      return;
    }
    if (!state.fieldMappings.enableAutoMapping) {
      commit('setAiProcessed', false);
      return;
    }
    commit("setMappingPreviewLoading", true);
    commit("setMappingSuggestionError", false);
    if (!state.fieldMappings.previewRows || state.fieldMappings.previewRows.length === 0) {
      commit("setMappingPreviewLoading", false);
      commit('setAiProcessed', false);
      return;
    }
    try {
      const firstLines = state.fieldMappings.previewRows.slice(0, 10).map(row => row.join(state.delimiter)).join('\n');
      const formData = new FormData();
      formData.append('firstLines', firstLines);
      const response = await axios.post('/import/validate-and-suggest-mapping', formData);
      if (response.data.success) {
        commit('setAiProcessed', true);
        const processedLines = response.data.processedCsv.split('\n').filter(line => line.trim());
        const processedRows = processedLines.map(line => {
          return line.split(state.delimiter);
        });
        if (processedRows.length > 0) {
          const headers = processedRows[0];
          const suggestedMapping = headers.map(header => {
            return ImportFieldType[header.toUpperCase()] || ImportFieldType.IGNORE;
          });
          commit('setValidatedPreviewRows', {
            rows: processedRows,
            validationInfo: {
              validRowCount: response.data.validRowCount,
              invalidRowCount: response.data.invalidRowCount,
              autoCorrections: response.data.autoCorrections,
              validationErrors: response.data.validationErrors,
            },
            suggestedMapping: suggestedMapping
          });
        } else {
          commit("setMappingSuggestionError", true);
          commit('setAiProcessed', false);
        }
      } else {
        commit("setMappingSuggestionError", true);
        commit('setAiProcessed', false);
      }
    } catch (error) {
      commit("setMappingSuggestionError", true);
      commit('setAiProcessed', false);
    } finally {
      commit("setMappingPreviewLoading", false);
      commit('setValidatedFileChecksum', state.checksum);
    }
  },
};
